import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { CampanhaStatus } from '../../core/enum/campanha-status.enum';
// Usados apenas pelo filtro de Tipo, hoje comentado.
// import { TipoCampanhaBack } from '../../core/enum/tipo-campanha-back.enum';
import { Campanha, CampanhaFiltros } from '../../core/models/campanha.model';
// import { TipoCampanha } from '../../core/models/tipo-campanha.model';
import { CampanhaService } from '../../core/services/campanha.service';
import { ToastService } from '../../core/services/toast.service';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { normalizarTexto } from '../../core/utils/texto.util';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { DataTableComponent } from '../../shared/components/data-table/data-table.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import {
  FilterBarComponent,
  FilterField,
} from '../../shared/components/filter-bar/filter-bar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';

const PAGE_SIZE = 10;

/** Termo só de dígitos: é o ID da campanha, não o nome. */
const SOMENTE_DIGITOS = /^\d+$/;

/**
 * A integração só acontece depois que a campanha termina: é quando o analista valida as
 * solicitações de troca de perfil e libera o envio. Por isso a listagem é fixa em Encerrada.
 */
const STATUS_ELEGIVEL_PARA_INTEGRACAO = CampanhaStatus.Encerrada;

const TODOS_OS_STATUS = 'todos';

/**
 * Opções do filtro de Status. Toda campanha desta tela tem o mesmo código ('EN'): o que
 * distingue uma da outra é o andamento da integração, que o back manda em `descricaoStatus` —
 * o mesmo texto exibido no badge. Por isso o filtro compara descrição, e não código de status.
 */
const STATUS_DA_INTEGRACAO = [
  'Aguardando Integração',
  'Integração Iniciada',
  'Integração Parcial',
  'Integração Concluída',
] as const;

@Component({
  selector: 'app-integracao-selecao',
  imports: [
    RouterLink,
    PageHeaderComponent,
    FilterBarComponent,
    DataTableComponent,
    StatusBadgeComponent,
    PaginationComponent,
    EmptyStateComponent,
  ],
  templateUrl: './integracao-selecao.component.html',
  styleUrl: './integracao-selecao.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntegracaoSelecaoComponent {
  private readonly campanhaService = inject(CampanhaService);
  private readonly toastService = inject(ToastService);
  private readonly loaderService = inject(LoaderService);

  private readonly campanhasSignal = signal<readonly Campanha[]>([]);
  private readonly termoBuscaSignal = signal('');
  private readonly statusSelecionadoSignal = signal<string>(TODOS_OS_STATUS);
  // private readonly tiposCampanhaSignal = signal<readonly TipoCampanha[]>([]);

  readonly currentPage = signal(1);
  readonly pageSize = PAGE_SIZE;
  readonly formatDate = formatDateIsoToBr;

  /**
   * Filtros da listagem de campanhas, menos Tipo (comentado abaixo). Segue `computed` porque o
   * de Tipo depende do catálogo de tipos.
   */
  readonly filterFields = computed<FilterField[]>(() => [
    {
      key: 'nome',
      label: 'Procure por nome ou ID',
      type: 'text',
      placeholder: 'Digite o nome ou o ID da campanha',
    },
    {
      key: 'descricaoStatus',
      label: 'Status',
      type: 'select',
      defaultValue: TODOS_OS_STATUS,
      options: [
        { value: TODOS_OS_STATUS, label: 'Todos' },
        ...STATUS_DA_INTEGRACAO.map((status) => ({ value: status, label: status })),
      ],
    },
    // Filtro de Tipo fora da tela por ora — a coluna correspondente também está comentada
    // no template. A listagem hoje só traz Suitability, então o filtro não separa nada.
    // {
    //   key: 'tipo',
    //   label: 'Tipo',
    //   type: 'select',
    //   defaultValue: 'todos',
    //   options: [
    //     { value: 'todos', label: 'Todos' },
    //     ...this.tiposCampanhaSignal()
    //       .filter((tipo) => tipo.codigo === TipoCampanhaBack.Suitability)
    //       .map((tipo) => ({ value: tipo.descricao, label: tipo.descricao })),
    //   ],
    // },
    {
      key: 'periodoDe',
      label: 'Período',
      type: 'daterange',
      keyTo: 'periodoAte',
    },
  ]);

  /**
   * O filtro de Status roda aqui, e não no endpoint: a descrição do andamento não é parâmetro
   * de busca da API — só vem na resposta.
   */
  readonly campanhasFiltradas = computed(() => {
    const selecionado = this.statusSelecionadoSignal();
    if (selecionado === TODOS_OS_STATUS) {
      return this.campanhasSignal();
    }

    const alvo = normalizarTexto(selecionado);
    return this.campanhasSignal().filter(
      (campanha) => normalizarTexto(campanha.statusLabel) === alvo,
    );
  });

  readonly campanhasPaginadas = computed(() => {
    const inicio = (this.currentPage() - 1) * PAGE_SIZE;
    return this.campanhasFiltradas().slice(inicio, inicio + PAGE_SIZE);
  });

  // Catálogo de tipos só alimentava o filtro de Tipo, hoje comentado.
  // constructor() {
  //   this.campanhaService.listarTiposCampanha().subscribe({
  //     next: (tipos) => this.tiposCampanhaSignal.set(tipos),
  //     error: () => this.tiposCampanhaSignal.set([]),
  //   });
  // }

  /** Chamado pelo `app-filter-bar` no submit e na inicialização. */
  onFiltersChange(record: Record<string, string>): void {
    this.currentPage.set(1);
    this.statusSelecionadoSignal.set(record['descricaoStatus'] || TODOS_OS_STATUS);

    const filtros: CampanhaFiltros = { status: STATUS_ELEGIVEL_PARA_INTEGRACAO };
    this.aplicarTermoDeBusca(filtros, record['nome'] ?? '');

    // Acompanha o filtro de Tipo, comentado em `filterFields`.
    // if (record['tipo'] && record['tipo'] !== 'todos') {
    //   filtros.tipo = record['tipo'];
    // }

    if (record['periodoDe']) {
      filtros.periodoDe = record['periodoDe'];
    }

    if (record['periodoAte']) {
      filtros.periodoAte = record['periodoAte'];
    }

    this.listarCampanhas(filtros);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  /**
   * O campo de busca é um só e atende os dois parâmetros do endpoint: um termo de dígitos é o
   * ID da campanha (`idCampanha`); qualquer outro é o nome (`nmCampanha`).
   */
  private aplicarTermoDeBusca(filtros: CampanhaFiltros, termo: string): void {
    const busca = termo.trim();
    if (!busca) {
      return;
    }

    if (SOMENTE_DIGITOS.test(busca)) {
      filtros.idCampanha = Number(busca);
      return;
    }

    filtros.nome = busca;
  }

  private listarCampanhas(filtros: CampanhaFiltros): void {
    this.loaderService.showLoader();

    this.campanhaService
      .listarCampanhas(filtros)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: (lista) => this.campanhasSignal.set(lista),
        error: (error: unknown) => {
          this.campanhasSignal.set([]);
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível carregar as campanhas.';
          this.toastService.show(message, 'error');
        },
      });
  }
}
